/**
 * Australian CGT rules (ITAA 1997 Pt 3-1/3-3), including the 2027 reform legislated in June 2026 (Act No. 49, 2026) that
 * applies to gains accruing from 1 July 2027. Not tax advice — see docs for assumptions a tax professional should confirm.
 */
import { Decimal, dec } from '../money.js';
import { addDays, addYears, diffDays, financialYearOf, parts } from '../dates.js';
import { KeelError, assertNever } from '../errors.js';
/** Assets acquired before 20 Sep 1985 are pre-CGT (exempt). */
export const AU_PRE_CGT_CUTOFF = '1985-09-20';
/** Indexation method available for assets acquired before 11:45am 21 Sep 1999 (date-only: before this date). */
export const AU_INDEXATION_CUTOFF = '1999-09-21';
export const AU_REGIME_2027_START = '2027-07-01';
export const AU_MIN_TAX_RATE_2027 = 0.3;
/**
 * ATO CPI index numbers for the indexation method, keyed by quarter-end month, frozen at the Sept 1999 quarter.
 * Figures from the ATO Guide to capital gains tax, Appendix 2 (Sept 1999 = 68.7). Use the ATO's rounded figures:
 * a rebased series rounds differently and changes 3-decimal factors.
 */
export const AU_CPI_QUARTERLY = {
    '1985-09': 39.7, '1985-12': 40.5,
    '1986-03': 41.4, '1986-06': 42.1, '1986-09': 43.2, '1986-12': 44.4,
    '1987-03': 45.3, '1987-06': 46.0, '1987-09': 46.8, '1987-12': 47.6,
    '1988-03': 48.4, '1988-06': 49.3, '1988-09': 50.2, '1988-12': 51.2,
    '1989-03': 51.7, '1989-06': 53.0, '1989-09': 54.2, '1989-12': 55.2,
    '1990-03': 56.2, '1990-06': 57.1, '1990-09': 57.5, '1990-12': 59.0,
    '1991-03': 58.9, '1991-06': 59.0, '1991-09': 59.3, '1991-12': 59.9,
    '1992-03': 59.9, '1992-06': 59.7, '1992-09': 59.8, '1992-12': 60.1,
    '1993-03': 60.6, '1993-06': 60.8, '1993-09': 61.1, '1993-12': 61.2,
    '1994-03': 61.5, '1994-06': 61.9, '1994-09': 62.3, '1994-12': 62.8,
    '1995-03': 63.8, '1995-06': 64.7, '1995-09': 65.5, '1995-12': 66.0,
    '1996-03': 66.2, '1996-06': 66.7, '1996-09': 66.9, '1996-12': 67.0,
    '1997-03': 67.1, '1997-06': 66.9, '1997-09': 66.6, '1997-12': 66.8,
    '1998-03': 67.0, '1998-06': 67.4, '1998-09': 67.5, '1998-12': 67.8,
    '1999-03': 67.8, '1999-06': 68.1, '1999-09': 68.7,
};
export const AU_CPI_FROZEN_QUARTER = '1999-09';
const roundHE = (d) => new Decimal(d).toDecimalPlaces(0, Decimal.ROUND_HALF_EVEN).toNumber();
const quarterKey = (d) => {
    const p = parts(d);
    return `${p.y}-${String(Math.ceil(p.m / 3) * 3).padStart(2, '0')}`;
};
/** ATO indexation factor (3 dp) for expenditure incurred on `date`; 1 when on/after the Sept 1999 quarter. */
export const indexationFactor = (date) => {
    const key = quarterKey(date);
    if (key >= AU_CPI_FROZEN_QUARTER)
        return dec(1);
    const cpi = AU_CPI_QUARTERLY[key];
    if (cpi === undefined)
        throw new KeelError('VALIDATION', `No indexation CPI for quarter ${key} (pre-CGT asset?)`);
    return dec(AU_CPI_QUARTERLY[AU_CPI_FROZEN_QUARTER] ?? 68.7).div(cpi).toDecimalPlaces(3, Decimal.ROUND_HALF_UP);
};
/** First disposal date that is discount eligible: acquisition + 12 months + 1 day (both days excluded). */
export const discountEligibleFrom = (acquiredDate) => addDays(addYears(acquiredDate, 1), 1);
export const isDiscountEligibleAU = (acquiredDate, disposalDate) => disposalDate >= discountEligibleFrom(acquiredDate);
export const daysUntilDiscount = (acquiredDate, onDate) => Math.max(0, diffDays(onDate, discountEligibleFrom(acquiredDate)));
/** CGT discount as an exact fraction. */
export const discountFraction = (entityType) => {
    switch (entityType) {
        case 'individual':
        case 'household':
        case 'trust':
        case 'partnership':
        case 'sole_trader':
            return dec(1).div(2);
        case 'smsf': return dec(1).div(3);
        case 'company':
        case 'club': return dec(0); // clubs/associations are taxed as companies
        default: return assertNever(entityType, 'entity type');
    }
};
/**
 * 2027 CGT reform — Treasury Laws Amendment (Tax Reform No. 1) Act 2026 (No. 49, 2026; assented 26 June 2026).
 * https://www.legislation.gov.au/C2026A00049/asmade/2026-06-26/text/original/pdf
 * Applies automatically to CGT events on/after 1 July 2027 for individuals (incl. partners) and trusts
 * (ss 110-36(1A), 112-155, 112-165, 115-100(aa)/(ab)/(f)); complying super funds keep the ⅓ discount and companies are
 * unchanged. Not modelled: foreign/temporary residents (s114-25), main residence days (s102-6(2)(d)), small business
 * concessions, affordable housing (s115-125).
 */
export const regime2027Applies = (date, ctx) => date >= AU_REGIME_2027_START && discountFraction(ctx.entityType).eq(0.5);
/** CPI index number for the quarter containing `date` (s960-275(1B)); null when the series lacks that quarter. */
export const cpiForQuarter = (series, date) => (series ?? []).find((p) => quarterKey(p.date) === quarterKey(date))?.index ?? null;
/** @deprecated use cpiForQuarter — latest observation on/before `date`. */
export const cpiAt = (series, date) => {
    let best;
    for (const p of series ?? [])
        if (p.date <= date && (!best || p.date > best.date))
            best = p;
    if (!best)
        throw new KeelError('VALIDATION', `cpiSeries has no value on/before ${date}`);
    return best.index;
};
/**
 * s960-275(1B): index number for the quarter of the CGT event ÷ index number for the quarter the expenditure was
 * incurred (the deemed reacquisition is incurred on 1 July 2027, i.e. the September 2027 quarter). Rounded to 3 dp and
 * never below 1 (both carried over from the pre-1999 method — confirm). Null when a quarter's CPI is unknown.
 */
export const indexationFactor2027 = (series, incurred, event) => {
    const a = cpiForQuarter(series, incurred), b = cpiForQuarter(series, event);
    if (a === null || b === null)
        return null;
    return Decimal.max(1, dec(b).div(a).toDecimalPlaces(3, Decimal.ROUND_HALF_UP));
};
/** Split one disposal under the 2027 rules. Same inputs → same result, so `summariseYear` can recompute it from the event. */
export const splitGain2027 = (e, ctx) => {
    const eligible = isDiscountEligibleAU(e.acquiredDate, e.date); // s114-10(1) and (9): 12 months from the real acquisition
    const isNewDwelling = (ctx.newResidentialDwellings ?? []).some((id) => id === e.securityId || id === e.parcelId);
    const nominal = e.proceedsCents - e.costBaseCents;
    if (isNewDwelling && !ctx.newDwellingIndexation) {
        return {
            deferredGainCents: 0, deferredLossCents: 0, postGainCents: Math.max(0, nominal), postLossCents: Math.max(0, e.reducedCostBaseCents - e.proceedsCents),
            newDwellingDiscount: true, deemedCostBaseCents: e.costBaseCents, indexedCostBaseCents: e.costBaseCents,
            valuation: 'not_applicable', valuationFallback: false, indexationUnavailable: false,
        };
    }
    const heldAcross = e.acquiredDate < AU_REGIME_2027_START;
    let deemed = e.costBaseCents, rcbFrom = e.reducedCostBaseCents, deferredGain = 0, deferredLoss = 0;
    let valuation = 'not_applicable', fallback = false;
    if (heldAcross) {
        const mvPerUnit = ctx.valueAt?.(e.securityId, '2027-06-30') ?? undefined;
        const wantMv = (ctx.valuation2027 ?? 'market_value') === 'market_value';
        if (wantMv && mvPerUnit !== undefined) {
            deemed = roundHE(dec(mvPerUnit).mul(e.units));
            valuation = 'market_value';
        }
        else {
            // ponytail: s112-185 method is to be set by legislative instrument; until then straight-line time apportionment.
            const total = Math.max(1, diffDays(e.acquiredDate, e.date));
            deemed = e.costBaseCents + roundHE(dec(nominal).mul(diffDays(e.acquiredDate, AU_REGIME_2027_START)).div(total));
            valuation = 'apportionment';
            fallback = wantMv;
        }
        if (e.acquiredDate >= AU_PRE_CGT_CUTOFF) { // s112-175: pre-CGT gain before 1 July 2027 is disregarded
            if (deemed > e.costBaseCents)
                deferredGain = deemed - e.costBaseCents;
            else if (deemed < e.reducedCostBaseCents)
                deferredLoss = e.reducedCostBaseCents - deemed;
        }
        rcbFrom = deemed;
    }
    const incurred = heldAcross ? AU_REGIME_2027_START : e.acquiredDate;
    const factor = eligible ? indexationFactor2027(ctx.cpiSeries, incurred, e.date) : dec(1);
    const indexed = roundHE(dec(deemed).mul(factor ?? 1));
    return {
        deferredGainCents: deferredGain, deferredLossCents: deferredLoss,
        postGainCents: Math.max(0, e.proceedsCents - indexed), postLossCents: Math.max(0, rcbFrom - e.proceedsCents),
        newDwellingDiscount: false, deemedCostBaseCents: deemed, indexedCostBaseCents: indexed,
        valuation, valuationFallback: fallback, indexationUnavailable: factor === null,
    };
};
/**
 * Indexed cost base of a slice: the parcel's original cost (current cost base less recorded adjustments) indexed from the
 * acquisition quarter, plus each `adjustments[]` amount indexed from its own quarter (post-Sept-1999 amounts at 1.000),
 * pro-rated to the slice. ponytail: adjustments are assumed to relate to the units still held (sells don't record
 * adjustments), so an adjustment made after a partial sale is slightly over-weighted.
 */
export const indexCostBase = (slice) => {
    const p = slice.parcel;
    const adjTotal = p.adjustments.reduce((t, a) => t + a.costBaseDeltaCents, 0);
    const full = p.adjustments.reduce((t, a) => t.plus(dec(a.costBaseDeltaCents).mul(indexationFactor(a.date))), dec(p.costBaseCents - adjTotal).mul(indexationFactor(p.acquiredDate)));
    const share = p.costBaseCents === 0 ? dec(1) : dec(slice.costBaseCents).div(p.costBaseCents);
    return Math.max(0, roundHE(full.mul(share)));
};
const computeDisposal = (slice, proceedsCents, date, ctx) => {
    const { parcel } = slice;
    const acquired = parcel.acquiredDate;
    const eligible = isDiscountEligibleAU(acquired, date);
    const base = {
        id: `cgt:${slice.tradeId ?? slice.corporateActionId ?? 'adhoc'}:${parcel.id}`,
        entityId: parcel.entityId, accountId: parcel.accountId, securityId: parcel.securityId, parcelId: parcel.id,
        tradeId: slice.tradeId, corporateActionId: slice.corporateActionId,
        date, acquiredDate: acquired, units: slice.units,
        proceedsCents, costBaseCents: slice.costBaseCents, reducedCostBaseCents: slice.reducedCostBaseCents,
        discountEligible: eligible, holdingDays: diffDays(acquired, date),
        financialYear: financialYearOf(date, ctx.fyStartMonth ?? 7, ctx.fyStartDay ?? 1).label,
        createdAt: slice.timestamp, updatedAt: slice.timestamp,
    };
    const zero = { ...base, grossGainCents: 0, isLoss: false, method: 'other', indexedCostBaseCents: null };
    if (regime2027Applies(date, ctx)) {
        const s = splitGain2027({
            securityId: parcel.securityId, parcelId: parcel.id, units: slice.units, acquiredDate: acquired, date, proceedsCents,
            costBaseCents: slice.costBaseCents, reducedCostBaseCents: slice.reducedCostBaseCents,
        }, ctx);
        const net = s.deferredGainCents - s.deferredLossCents + s.postGainCents - s.postLossCents;
        const indexedUsed = !s.newDwellingDiscount && s.indexedCostBaseCents !== s.deemedCostBaseCents;
        return {
            ...base, grossGainCents: net, isLoss: net < 0,
            method: s.newDwellingDiscount ? (eligible ? 'discount' : 'other') : indexedUsed ? 'indexation' : 'other',
            indexedCostBaseCents: indexedUsed ? s.indexedCostBaseCents : null,
            valuation2027: s.valuation === 'not_applicable' ? null : s.valuation,
        };
    }
    if (acquired < AU_PRE_CGT_CUTOFF)
        return zero;
    if (proceedsCents < slice.reducedCostBaseCents) {
        return { ...zero, grossGainCents: proceedsCents - slice.reducedCostBaseCents, isLoss: true };
    }
    const nominal = proceedsCents - slice.costBaseCents;
    if (nominal <= 0)
        return zero;
    const discount = discountFraction(ctx.entityType);
    if (parcel.isPre1999 && eligible) {
        const indexed = indexCostBase(slice);
        const indexedGain = Math.max(0, proceedsCents - indexed);
        // ponytail: chooses per event ignoring loss interaction (losses reduce the value of the discount).
        if (dec(indexedGain).lt(dec(nominal).mul(dec(1).minus(discount)))) {
            return { ...base, grossGainCents: indexedGain, isLoss: false, method: 'indexation', indexedCostBaseCents: indexed };
        }
    }
    return { ...base, grossGainCents: nominal, isLoss: false, method: eligible && discount.gt(0) ? 'discount' : 'other', indexedCostBaseCents: null };
};
/** Apply `loss` against non-discount (`nd`) and discountable (`disc`) gains in the chosen order. */
export const applyLosses = (loss, nd, disc, order) => {
    const used = Math.min(loss, nd + disc);
    switch (order) {
        case 'non_discount_first': {
            const a = Math.min(used, nd);
            return { nd: nd - a, disc: disc - (used - a), used };
        }
        case 'discount_first': {
            const b = Math.min(used, disc);
            return { nd: nd - (used - b), disc: disc - b, used };
        }
        case 'pro_rata': {
            if (used === 0)
                return { nd, disc, used };
            const a = Math.min(nd, Math.max(used - disc, roundHE(dec(used).mul(nd).div(nd + disc))));
            return { nd: nd - a, disc: disc - (used - a), used };
        }
        default: return assertNever(order, 'loss order');
    }
};
/** Two-category loss application (s102-5(1) step 1): deferred gains first, then other gains; `order` within a category. */
const applyLossesByCategory = (loss, def, other, order) => {
    const a = applyLosses(loss, def.nd, def.disc, order);
    const b = applyLosses(loss - a.used, other.nd, other.disc, order);
    return { def: { nd: a.nd, disc: a.disc }, other: { nd: b.nd, disc: b.disc }, used: a.used + b.used };
};
const summariseYear = (events, carriedForwardInCents, ctx, opts = {}) => {
    const evs = opts.fy ? events.filter((e) => e.financialYear === opts.fy) : [...events];
    const order = opts.lossOrder ?? 'non_discount_first';
    // deferred = s102-6(3)/(4) gains from the 30 June 2027 deemed sale; other = everything else.
    // `post` tracks indexed post-1-July-2027 gains, which are the minimum tax capital gain base (s119-5).
    const def = { nd: 0, disc: 0 }, other = { nd: 0, disc: 0 };
    let losses = 0, post = 0, applied = false, fallback = false, noCpi = false;
    for (const e of evs) {
        if (regime2027Applies(e.date, ctx)) {
            applied = true;
            const s = splitGain2027(e, ctx);
            fallback ||= s.valuationFallback;
            noCpi ||= s.indexationUnavailable;
            losses += s.deferredLossCents + s.postLossCents;
            if (e.discountEligible)
                def.disc += s.deferredGainCents;
            else
                def.nd += s.deferredGainCents;
            if (s.newDwellingDiscount && e.discountEligible)
                other.disc += s.postGainCents;
            else if (s.newDwellingDiscount)
                other.nd += s.postGainCents;
            else {
                other.nd += s.postGainCents;
                post += s.postGainCents;
            }
            continue;
        }
        if (e.isLoss) {
            losses += -e.grossGainCents;
            continue;
        }
        if (e.grossGainCents <= 0)
            continue;
        if (e.discountEligible && e.method !== 'indexation')
            other.disc += e.grossGainCents;
        else
            other.nd += e.grossGainCents;
    }
    const trustDisc = Math.max(0, opts.trustGains?.discountableCents ?? 0), trustNd = Math.max(0, opts.trustGains?.nonDiscountCents ?? 0);
    other.disc += trustDisc;
    other.nd += trustNd;
    const gross = def.disc + def.nd + other.disc + other.nd;
    const cur = applyLossesByCategory(losses, def, other, order);
    const cfIn = Math.max(0, carriedForwardInCents);
    const cf = applyLossesByCategory(cfIn, cur.def, cur.other, order);
    const discPool = cf.def.disc + cf.other.disc;
    const discountedRemainder = roundHE(dec(discPool).mul(dec(1).minus(discountFraction(ctx.entityType))));
    const discountCents = discPool - discountedRemainder;
    const net = cf.def.nd + cf.other.nd + discountedRemainder;
    const cfOut = (cfIn - cf.used) + (losses - cur.used);
    // ponytail: within the non-deferred non-discount pool, losses are assumed to reach indexed post-2027 gains last.
    const postNet = Math.min(post, cf.other.nd);
    return {
        fy: opts.fy ?? evs[0]?.financialYear ?? '',
        grossGainsCents: gross, trustCapitalGainsCents: trustDisc + trustNd,
        discountableGainsCents: def.disc + other.disc, nonDiscountGainsCents: def.nd + other.nd,
        currentYearLossesCents: losses, lossesAppliedCents: cur.used,
        carriedForwardInCents: cfIn, carriedForwardAppliedCents: cf.used,
        discountCents, netCapitalGainCents: net, carriedForwardOutCents: cfOut,
        postRegimeGainCents: postNet,
        regime2027: applied ? { valuation: ctx.valuation2027 ?? 'market_value', valuationFallback: fallback, indexationUnavailable: noCpi } : null,
        atoLabels: { '18H': gross, '18V': cfOut, '18A': net },
        events: evs,
        jurisdiction: 'AU',
        labels: { '18H': gross, '18V': cfOut, '18A': net },
    };
};
/** Effective tax rate on capital gains for the entity (AU). */
export const effectiveCgtRate = (ctx) => {
    switch (ctx.entityType) {
        case 'smsf': return 0.15 * (1 - (ctx.pensionPhaseProportion ?? 0));
        case 'company':
        case 'club': return ctx.marginalRate === 0.25 ? 0.25 : 0.3; // base-rate entity 25 %, otherwise 30 %
        case 'individual':
        case 'household':
        case 'trust':
        case 'partnership':
        case 'sole_trader':
            return ctx.marginalRate + ctx.medicareLevy;
        default: return assertNever(ctx.entityType, 'entity type');
    }
};
/**
 * Division 119 minimum tax gap amount (s119-10(2)): 30 % of the minimum tax capital gain less the income tax that gain
 * adds at the taxpayer's rate (approximated as `marginalRate`, excluding the Medicare levy), rounded down to whole dollars.
 * Individuals (and, through s115-215, trust beneficiaries); nil for recipients of the payments in s119-15.
 */
export const minimumTaxTopUp = (minimumTaxCapitalGainCents, ctx) => {
    if (ctx.minimumTaxExempt || minimumTaxCapitalGainCents <= 0 || !discountFraction(ctx.entityType).eq(0.5))
        return 0;
    const gap = dec(minimumTaxCapitalGainCents).mul(Math.max(0, AU_MIN_TAX_RATE_2027 - ctx.marginalRate));
    return gap.div(100).floor().mul(100).toNumber();
};
/** Estimated tax on a net capital gain plus the Division 119 top-up on `postRegimeGainCents` (the minimum tax capital gain). */
export const estimateTax = (netCapitalGainCents, ctx, opts = {}) => {
    if (netCapitalGainCents <= 0)
        return 0;
    const post = Math.min(Math.max(0, opts.postRegimeGainCents ?? 0), netCapitalGainCents);
    return roundHE(dec(netCapitalGainCents).mul(effectiveCgtRate(ctx))) + minimumTaxTopUp(post, ctx);
};
export const auJurisdiction = {
    code: 'AU',
    // s115-100(f): no discount for individuals/trusts on CGT events from 1 July 2027 (deferred gains and new dwellings excepted)
    discountRate: (entityType, date) => (date >= AU_REGIME_2027_START && discountFraction(entityType).eq(0.5) ? 0 : discountFraction(entityType).toNumber()),
    isDiscountEligible: isDiscountEligibleAU,
    computeDisposal,
    summariseYear,
    estimateTax: (s, ctx) => estimateTax(s.netCapitalGainCents, ctx, { postRegimeGainCents: s.postRegimeGainCents }),
    marginalGainRate: (ctx, eligible) => effectiveCgtRate(ctx) * (eligible ? 1 - discountFraction(ctx.entityType).toNumber() : 1),
};
//# sourceMappingURL=au.js.map